import { SELECTION_COLUMN_ID } from '../table/components/selection-column-id'

export type StickySide = 'end' | 'start'

type ColumnLookup = {
  getColumn: (id: string) => undefined | { getIsVisible: () => boolean; getSize: () => number }
}

type ColumnPinningState = { end: string[]; start: string[] }

const MAX_PINNED_WIDTH_RATIO = 0.5

/**
 * The side each sticky column sticks to. Given a viewport width, pinned columns that don't fit
 * half of it scroll instead and are left out; without one, every visible pinned column sticks.
 */
export const getStickySides = (
  table: ColumnLookup,
  pinning: ColumnPinningState,
  columnSizing: Record<string, number>,
  viewportWidth: null | number,
): ReadonlyMap<string, StickySide> => {
  const getVisibleWidths = (ids: string[]) =>
    ids.flatMap(id => {
      const column = table.getColumn(id)
      if (!column?.getIsVisible()) return []
      return [{ id, width: columnSizing[id] ?? column.getSize() }]
    })

  const start = getVisibleWidths(pinning.start)
  const end = getVisibleWidths(pinning.end)
  const toSides = (startColumns: { id: string }[], endColumns: { id: string }[]) =>
    new Map<string, StickySide>([
      ...startColumns.map(column => [column.id, 'start'] as const),
      ...endColumns.map(column => [column.id, 'end'] as const),
    ])

  if (viewportWidth === null) return toSides(start, end)

  const budget = viewportWidth * MAX_PINNED_WIDTH_RATIO
  const totalWidth = [...start, ...end].reduce((sum, column) => sum + column.width, 0)

  if (totalWidth <= budget) return toSides(start, end)

  const stickyEnd: { id: string }[] = []
  let usedEndWidth = 0

  for (let index = end.length - 1; index >= 0; index -= 1) {
    const column = end[index]
    const nextWidth = usedEndWidth + column.width
    if (nextWidth > budget) break
    usedEndWidth = nextWidth
    stickyEnd.unshift(column)
  }

  const remainingBudget = budget - usedEndWidth
  const getLeadingRun = (columns: typeof start) => {
    const run: typeof start = []
    let usedWidth = 0

    for (const column of columns) {
      const nextWidth = usedWidth + column.width
      if (nextWidth > remainingBudget) break
      usedWidth = nextWidth
      run.push(column)
    }

    return run
  }
  const countIdentifying = (columns: { id: string }[]) =>
    columns.filter(column => column.id !== SELECTION_COLUMN_ID).length

  // A bare checkbox doesn't tell rows apart once the table scrolls, so it yields only when
  // keeping it would leave no identifying column sticky.
  const withSelection = getLeadingRun(start)
  const withoutSelection = getLeadingRun(start.filter(column => column.id !== SELECTION_COLUMN_ID))
  const stickyStart =
    countIdentifying(withSelection) === 0 && countIdentifying(withoutSelection) > 0
      ? withoutSelection
      : withSelection

  return toSides(stickyStart, stickyEnd)
}
